import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { BusReservation } from '../../types'

interface BusTrip {
  id: string
  destination: string
  date: string
  departureTime: string
  returnTime: string
  departureLocation: string
  price: number
  maxSeats: number
  bookedSeats: number
  description: string
  status: 'open' | 'full' | 'completed' | 'cancelled'
}

interface BusStore {
  busTrips: BusTrip[]
  reservations: BusReservation[]
  addBusTrip: (trip: Omit<BusTrip, 'id' | 'bookedSeats'>) => void
  updateBusTrip: (id: string, updates: Partial<BusTrip>) => void
  deleteBusTrip: (id: string) => void
  addReservation: (reservation: Omit<BusReservation, 'id' | 'status'>) => void
  updateReservation: (id: string, updates: Partial<BusReservation>) => void
  updateReservationStatus: (id: string, status: 'pending' | 'approved' | 'rejected') => void
  deleteReservation: (id: string) => void
  getTripById: (id: string) => BusTrip | undefined
  getReservationsByTrip: (tripId: string) => BusReservation[]
  getAvailableSeats: (tripId: string) => number
  isDateAvailable: (date: string, excludeId?: string) => boolean
}

const useBusStore = create<BusStore>()(
  persist(
    (set, get) => ({
      busTrips: [
        {
          id: '1',
          destination: 'Auswärtsspiel vs. FC Waldorf',
          date: '2025-02-22',
          departureTime: '13:00',
          returnTime: '18:00',
          departureLocation: 'Vereinshaus FC Frick',
          price: 15.00,
          maxSeats: 50,
          bookedSeats: 23,
          description: 'Busfahrt zum Auswärtsspiel gegen FC Waldorf',
          status: 'open',
        },
        {
          id: '2',
          destination: 'Trainingslager Schwarzwald',
          date: '2025-03-15',
          departureTime: '08:00',
          returnTime: '2025-03-17 20:00',
          departureLocation: 'Vereinshaus FC Frick',
          price: 45.00,
          maxSeats: 40,
          bookedSeats: 12,
          description: 'Dreitägiges Trainingslager im Schwarzwald',
          status: 'open',
        },
        {
          id: '3',
          destination: 'Turnier Stuttgart',
          date: '2025-01-20',
          departureTime: '07:00',
          returnTime: '22:00',
          departureLocation: 'Vereinshaus FC Frick',
          price: 25.00,
          maxSeats: 50,
          bookedSeats: 50,
          description: 'Teilnahme am Hallenturnier in Stuttgart',
          status: 'completed',
        },
      ],

      reservations: [],

      addBusTrip: (trip) => {
        const newTrip: BusTrip = {
          ...trip,
          id: Date.now().toString(),
          bookedSeats: 0,
        }
        set((state) => ({
          busTrips: [...state.busTrips, newTrip]
        }))
      },

      updateBusTrip: (id, updates) => {
        set((state) => ({
          busTrips: state.busTrips.map(trip =>
            trip.id === id ? { ...trip, ...updates } : trip
          )
        }))
      },

      deleteBusTrip: (id) => {
        set((state) => ({
          busTrips: state.busTrips.filter(trip => trip.id !== id),
          reservations: state.reservations.filter(res => 
            !res.destination.includes(state.busTrips.find(t => t.id === id)?.destination || '')
          )
        }))
      },

      addReservation: (reservation) => {
        const newReservation: BusReservation = {
          ...reservation,
          id: Date.now().toString(),
          status: 'pending',
        }
        set((state) => ({
          reservations: [...state.reservations, newReservation]
        }))
      },

      updateReservation: (id, updates) => {
        set((state) => ({
          reservations: state.reservations.map(res =>
            res.id === id ? { ...res, ...updates } : res
          )
        }))
      },

      updateReservationStatus: (id, status) => {
        set((state) => ({
          reservations: state.reservations.map(res =>
            res.id === id ? { ...res, status } : res
          )
        }))
      },

      deleteReservation: (id) => {
        set((state) => ({
          reservations: state.reservations.filter(res => res.id !== id)
        }))
      },

      getTripById: (id) => {
        return get().busTrips.find(trip => trip.id === id)
      },

      getReservationsByTrip: (tripId) => {
        const trip = get().busTrips.find(t => t.id === tripId)
        if (!trip) return []
        return get().reservations.filter(res => 
          res.destination.includes(trip.destination)
        )
      },

      getAvailableSeats: (tripId) => {
        const trip = get().busTrips.find(t => t.id === tripId)
        return trip ? trip.maxSeats - trip.bookedSeats : 0
      },

      isDateAvailable: (date, excludeId) => {
        const { busTrips } = get()
        return !busTrips.some(trip => 
          trip.date === date && 
          (!excludeId || trip.id !== excludeId)
        )
      },
    }),
    {
      name: 'fc-frick-bus',
    }
  )
)

export { useBusStore }
